import { useCallback, useEffect, useId, useMemo, useState } from 'react'
import type { ChangeEvent, ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Activity, ArrowLeft, RotateCcw, Trash2, ChevronDown, ChevronLeft, ChevronRight, CheckCircle, Circle, Star, Timer, Map as MapIcon, Shuffle, Upload, UserCircle, MonitorSmartphone, Mail, KeyRound, Download, Loader2, RefreshCw } from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Button } from '../components/ui/button'
import { MeasurementField } from '../components/ui/measurement'
import { useMeasurements } from '../hooks/useMeasurements'
import { useAuth } from '../hooks/useAuth'
import { WorkoutGpsDebugView } from '../components/debug/WorkoutGpsDebugView'
import {
  deleteMobileUser,
  createMobileUserImpersonationLink,
  resetMobileUser,
  sendMobileUserPasswordReset,
  setMobileUserPassword,
  EnduranceFrequency,
  EquipmentAccessOption,
  getMobileUser,
  getUserActivityLog,
  getTrainingPlan,
  regenerateMobileUserTrainingPlan,
  reconcileMobileUserWorkoutProjections,
  getTrainingPlanWeek,
  getUserWorkouts,
  getUserWorkoutGpsTrace,
  AdminUserWorkoutSummary,
  WorkoutGpsTrace,
  HikeConcern,
  HikingFrequency,
  MeasurementUnit,
  MobileUser,
  OnboardingData,
  PackWeightOption,
  PlanGenerationTraceEntry,
  TrainingPlan,
  TrainingWeekDetail,
  TrueElevationWorkoutAccess,
  UserActivityLogEntry,
  updateMobileUser,
  setMobileUserSubscription,
  resetUsernameCooldown,
  uploadMobileUserProfileImage,
  setMobileUserDefaultProfilePicture,
  updateMobileUserIdentity,
  AgeRange,
  type WorkoutProjectionReconciliationResult
} from '../lib/api/adminMobileUsers'
import {
  getDefaultProfilePictures,
  type DefaultProfilePicture
} from '../lib/api/defaultProfilePictures'
import { formatWorkoutExerciseDetails } from '../lib/workoutExerciseDetails'
import { downloadCsv } from '../utils/csv'
import { getMobileWebBaseUrl } from '../utils/env'

type MeasurementHelpers = ReturnType<typeof useMeasurements>

const formatDateTime = (value?: string | null) => {
  if (!value) return '—'
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(new Date(value))
  } catch {
    return value
  }
}

const formatLoginType = (value?: string | null) => {
  switch (value) {
    case 'Apple':
      return 'Apple'
    case 'Google':
      return 'Google'
    case 'Facebook':
      return 'Facebook'
    case 'EmailPassword':
    default:
      return 'Email/password'
  }
}

const getApiErrorMessage = (err: unknown, fallback: string) => {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { message?: string } } }).response
    return response?.data?.message ?? fallback
  }

  return fallback
}

const formatProjectionReconciliationResult = (
  result: WorkoutProjectionReconciliationResult,
  refreshFailed: boolean
) => {
  const countLabel = (count: number, singular: string, plural = `${singular}s`) =>
    `${count} ${count === 1 ? singular : plural}`
  const legacyPlannerGhostCompletionsReset = result.legacyPlannerGhostCompletionsReset ?? 0
  const changes =
    legacyPlannerGhostCompletionsReset +
    result.projectionStatesAdded +
    result.projectionStatesReset +
    result.projectionAttemptsStarted
  const refreshSuffix = refreshFailed
    ? ' The repair completed, but refreshed plan details are temporarily unavailable.'
    : ''

  if (changes === 0) {
    return `Reconciliation complete. No changes needed after scanning ${result.workoutsScanned} workout records.${refreshSuffix}`
  }

  return `Reconciliation complete: ${countLabel(result.workoutsScanned, 'workout')} scanned; ${countLabel(legacyPlannerGhostCompletionsReset, 'legacy ghost completion')} reset; ${countLabel(result.projectionStatesAdded, 'projection state')} added; ${countLabel(result.projectionStatesReset, 'projection state')} reset; ${countLabel(result.projectionAttemptsStarted, 'projection attempt')} started.${refreshSuffix}`
}

const buildMobileWebImpersonationUrl = (ticket: string, mobileWebBaseUrl: string) => {
  const url = new URL('/support-login', mobileWebBaseUrl)
  url.searchParams.set('ticket', ticket)
  return url.toString()
}

const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

const enduranceOptions: { label: string; value: EnduranceFrequency }[] = [
  { label: '4+ days/week', value: 'FourPlusDays' },
  { label: '2-3 days/week', value: 'TwoThreeDays' },
  { label: '1 day or less/week', value: 'OneDayOrLess' },
  { label: 'Occasionally', value: 'Occasionally' },
  { label: 'Not at all', value: 'NotAtAll' }
]

const hikingOptions: { label: string; value: HikingFrequency }[] = [
  { label: '3+ days/week', value: 'ThreePlusDays' },
  { label: '1-2 days/week', value: 'OneTwoDays' },
  { label: '1-3 days/month', value: 'OneThreeDaysMonth' },
  { label: 'A few times', value: 'FewTimes' },
  { label: 'Not at all', value: 'NotAtAll' }
]

const measurementOptions: { label: string; value: MeasurementUnit }[] = [
  { label: 'Metric (km, m, kg)', value: 'Metric' },
  { label: 'Imperial (mi, ft, lb)', value: 'Imperial' }
]

const elevationWorkoutOptions: { label: string; value: TrueElevationWorkoutAccess }[] = [
  { label: 'Stairs', value: 'Stairs' },
  { label: 'Step-ups', value: 'StepUps' },
  { label: 'Local hikes/trails', value: 'LocalHikesOrTrails' },
  { label: 'Hills', value: 'Hills' }
]

const equipmentOptions: { label: string; value: EquipmentAccessOption }[] = [
  { label: 'Treadmill with incline', value: 'TreadmillWithIncline' },
  { label: 'Stair machine', value: 'StairMachine' }
]

const packWeightOptions: { label: string; value: PackWeightOption }[] = [
  { label: 'None', value: 'None' },
  { label: 'Day pack', value: 'Day' },
  { label: 'Ultralight', value: 'Ultralight' },
  { label: 'Lightweight', value: 'Lightweight' },
  { label: 'Traditional', value: 'Traditional' },
  { label: 'Unknown', value: 'Unknown' },
  { label: 'Custom', value: 'Custom' }
]

const concernOptions: { label: string; value: HikeConcern }[] = [
  { label: 'Endurance', value: 'Endurance' },
  { label: 'Joints', value: 'Joints' },
  { label: 'Altitude', value: 'Altitude' },
  { label: 'Strength', value: 'Strength' },
  { label: 'Other', value: 'Other' }
]

const activityEventOptions = [
  'exercise_added',
  'exercise_updated',
  'exercise_deleted',
  'exercise_started',
  'exercise_finished',
  'exercise_marked_complete',
  'plan_reset',
  'plan_changed',
  'post_created',
  'reply_created',
  'group_joined',
  'group_left',
  'group_created',
  'photo_posted',
  'profile_photo_changed',
  'profile_name_changed',
  'session_started'
]

const formatActivityEventLabel = (eventType: string) =>
  eventType
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

const formatActivityPropertyValue = (value: unknown): string => {
  if (value == null) return 'null'
  if (Array.isArray(value)) return value.map(formatActivityPropertyValue).join(', ')
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'number' || typeof value === 'string') return String(value)
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

type FormState = {
  username: string
  onboardingData: OnboardingData
}

const defaultForm = (): FormState => ({
  username: '',
  onboardingData: {
    fullName: null,
    ageRange: null,
    trainingDaysPerWeek: null,
    preferredDays: [],
    enduranceFrequency: null,
    hikingFrequency: null,
    measurementUnit: null,
    trueElevationWorkouts: [],
    equipmentAccess: [],
    packWeightOption: null,
    packWeightKg: null,
    hikeDate: null,
    hikeDateUnknown: null,
    concerns: [],
    otherConcerns: null
  }
})

const CollapsibleCard = ({
  title,
  description,
  icon,
  actions,
  className,
  contentClassName,
  defaultExpanded = true,
  children
}: {
  title: string
  description?: ReactNode
  icon?: ReactNode
  actions?: ReactNode
  className?: string
  contentClassName?: string
  defaultExpanded?: boolean
  children: ReactNode
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const contentId = useId()

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="min-w-0 flex-1 space-y-1">
          <CardTitle className="flex items-center gap-2">
            {icon}
            <span>{title}</span>
          </CardTitle>
          {description ? <CardDescription>{description}</CardDescription> : null}
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {actions}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
            aria-controls={contentId}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${title}`}
          >
            <ChevronDown
              className={`h-4 w-4 transition-transform ${expanded ? 'rotate-0' : '-rotate-90'}`}
              aria-hidden="true"
            />
          </Button>
        </div>
      </CardHeader>
      {expanded ? (
        <CardContent id={contentId} className={contentClassName}>
          {children}
        </CardContent>
      ) : null}
    </Card>
  )
}

const getTrainingPlanExportRevisionKey = (plan: TrainingPlan) => JSON.stringify({
  id: plan.id,
  updatedAt: plan.updatedAt ?? null,
  targets: plan.targets,
  weeks: plan.weeks.map((week) => ({
    weekNumber: week.weekNumber,
    targetDistanceKm: week.targetDistanceKm,
    targetElevationGainMeters: week.targetElevationGainMeters,
    targetPackWeightKg: week.targetPackWeightKg,
    actualDistanceKm: week.actualDistanceKm,
    actualElevationGainMeters: week.actualElevationGainMeters,
    workoutCount: week.workoutCount,
    completedWorkoutCount: week.completedWorkoutCount,
    revisionState: week.revisionState,
    coachNoteCount: week.coachNoteCount,
    generationTraceCount: week.generationTraceCount
  }))
})

const loadCoherentTrainingPlanExportSnapshot = async (userId: string) => {
  const maxAttempts = 3
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const planBefore = await getTrainingPlan(userId)
    if (!planBefore) {
      throw new Error('The user has no active training plan to export.')
    }

    const weekDetails = await Promise.all(
      planBefore.weeks.map(async (week) => getTrainingPlanWeek(userId, week.weekNumber))
    )
    const planAfter = await getTrainingPlan(userId)
    if (
      planAfter
      && getTrainingPlanExportRevisionKey(planBefore) === getTrainingPlanExportRevisionKey(planAfter)
    ) {
      return {
        plan: planAfter,
        weekDetails: weekDetails.filter((week): week is TrainingWeekDetail => week != null)
      }
    }
  }

  throw new Error('The training plan kept changing during export. Please try again.')
}

export const MobileUserDetailPage = () => {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const { user: authUser } = useAuth()
  const measurements = useMeasurements()
  const mobileWebBaseUrl = getMobileWebBaseUrl()
  const [user, setUser] = useState<MobileUser | null>(null)
  const [form, setForm] = useState<FormState>(defaultForm())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [reconcilingProjections, setReconcilingProjections] = useState(false)
  const [sendingPasswordReset, setSendingPasswordReset] = useState(false)
  const [settingPassword, setSettingPassword] = useState(false)
  const [savingLoginEmail, setSavingLoginEmail] = useState(false)
  const [manualPassword, setManualPassword] = useState('')
  const [loginEmail, setLoginEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showResetConfirm, setShowResetConfirm] = useState(false)
  const [showProjectionReconcileConfirm, setShowProjectionReconcileConfirm] = useState(false)
  const [togglingSubscription, setTogglingSubscription] = useState(false)
  const [coachNotesExpanded, setCoachNotesExpanded] = useState(false)
  const [savingAccountInfo, setSavingAccountInfo] = useState(false)
  const [plan, setPlan] = useState<TrainingPlan | null>(null)
  const [planLoading, setPlanLoading] = useState(false)
  const [exportingPlanMarkdown, setExportingPlanMarkdown] = useState(false)
  const [currentWeekNum, setCurrentWeekNum] = useState(1)
  const [weekDetail, setWeekDetail] = useState<TrainingWeekDetail | null>(null)
  const [weekDetailLoading, setWeekDetailLoading] = useState(false)
  const [defaultProfilePictures, setDefaultProfilePictures] = useState<DefaultProfilePicture[]>([])
  const [defaultProfilePicturesExpanded, setDefaultProfilePicturesExpanded] = useState(false)
  const [defaultProfilePicturesLoading, setDefaultProfilePicturesLoading] = useState(false)
  const [profileImageSaving, setProfileImageSaving] = useState<string | null>(null)
  const [openingWebSim, setOpeningWebSim] = useState(false)
  const authRole = (authUser?.role ?? 'Guest').toString()
  const canReconcileProjections = authRole === 'Admin' || authRole === 'Editor'

  const loadUser = useCallback(async () => {
    if (!userId) return
    setLoading(true)
    setError(null)
    try {
      const data = await getMobileUser(userId)
      setUser(data)
      setLoginEmail(data.email ?? '')
      setForm({
        username: data.username ?? '',
        onboardingData: {
          fullName: data.fullName ?? data.onboardingData?.fullName ?? null,
          ageRange: data.onboardingData?.ageRange ?? null,
          trainingDaysPerWeek: data.onboardingData?.trainingDaysPerWeek ?? null,
          preferredDays: data.onboardingData?.preferredDays ?? [],
          enduranceFrequency: data.onboardingData?.enduranceFrequency ?? null,
          hikingFrequency: data.onboardingData?.hikingFrequency ?? null,
          measurementUnit: data.onboardingData?.measurementUnit ?? null,
          trueElevationWorkouts: data.onboardingData?.trueElevationWorkouts ?? [],
          equipmentAccess: data.onboardingData?.equipmentAccess ?? [],
          packWeightOption: data.onboardingData?.packWeightOption ?? null,
          packWeightKg: data.onboardingData?.packWeightKg ?? null,
          hikeDate: data.onboardingData?.hikeDate ?? null,
          hikeDateUnknown: data.onboardingData?.hikeDateUnknown ?? null,
          concerns: data.onboardingData?.concerns ?? [],
          otherConcerns: data.onboardingData?.otherConcerns ?? null
        }
      })
    } catch (err) {
      console.error('Failed to load user', err)
      setError('Unable to load user. They may have been deleted.')
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    void loadUser()
  }, [loadUser])

  const loadDefaultProfilePictures = useCallback(async () => {
    setDefaultProfilePicturesLoading(true)
    try {
      setDefaultProfilePictures(await getDefaultProfilePictures({ activeOnly: true }))
    } catch (err) {
      console.error('Failed to load default profile pictures', err)
    } finally {
      setDefaultProfilePicturesLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadDefaultProfilePictures()
  }, [loadDefaultProfilePictures])

  const loadPlan = useCallback(async () => {
    if (!userId) return
    setPlanLoading(true)
    try {
      const data = await getTrainingPlan(userId)
      setPlan(data)
      if (data) {
        setCurrentWeekNum(data.currentWeek)
      }
    } catch (err) {
      console.error('Failed to load training plan', err)
    } finally {
      setPlanLoading(false)
    }
  }, [userId])

  useEffect(() => {
    void loadPlan()
  }, [loadPlan])

  const loadWeekDetail = useCallback(async (weekNum: number) => {
    if (!userId) return
    setWeekDetailLoading(true)
    setWeekDetail(null)
    try {
      const data = await getTrainingPlanWeek(userId, weekNum)
      setWeekDetail(data)
    } catch (err) {
      console.error('Failed to load week detail', err)
    } finally {
      setWeekDetailLoading(false)
    }
  }, [userId])

  useEffect(() => {
    if (plan) {
      void loadWeekDetail(currentWeekNum)
    }
  }, [plan, currentWeekNum, loadWeekDetail])

  const handleExportTrainingPlanMarkdown = useCallback(async () => {
    if (!userId || !user || !plan || exportingPlanMarkdown) return

    setExportingPlanMarkdown(true)
    setFeedback(null)
    try {
      const exportSnapshot = await loadCoherentTrainingPlanExportSnapshot(userId)
      const exportedAt = new Date()
      const markdown = buildGeneratedPlanMarkdownExport({
        exportedAt,
        user,
        plan: exportSnapshot.plan,
        weekDetails: exportSnapshot.weekDetails,
        measurements
      })

      downloadMarkdownFile(
        createGeneratedPlanExportFilename(user, exportSnapshot.plan, exportedAt),
        markdown
      )
      setFeedback({ type: 'success', message: 'Training plan markdown downloaded.' })
    } catch (err) {
      console.error('Failed to export training plan markdown', err)
      setFeedback({ type: 'error', message: 'Unable to export training plan markdown.' })
    } finally {
      setExportingPlanMarkdown(false)
    }
  }, [exportingPlanMarkdown, measurements, plan, user, userId])

  const handleSave = async () => {
    if (!userId) return
    const frequencyChanged =
      (user?.onboardingData?.enduranceFrequency ?? null) !== (form.onboardingData.enduranceFrequency ?? null) ||
      (user?.onboardingData?.hikingFrequency ?? null) !== (form.onboardingData.hikingFrequency ?? null)
    const shouldRegeneratePlan = Boolean(frequencyChanged && plan && !plan.isManual)
    if (shouldRegeneratePlan && !window.confirm(
      'These activity frequency changes affect the training-plan targets. Save the profile and regenerate the active plan now? Completed workout history will be preserved.'
    )) {
      return
    }

    setSaving(true)
    setFeedback(null)
    try {
      const updated = await updateMobileUser(userId, {
        username: form.username.trim() || null,
        onboardingData: form.onboardingData
      })
      setUser(updated)

      if (shouldRegeneratePlan) {
        try {
          const regeneratedPlan = await regenerateMobileUserTrainingPlan(userId, {
            enduranceFrequency: form.onboardingData.enduranceFrequency,
            hikingFrequency: form.onboardingData.hikingFrequency
          })
          setPlan(regeneratedPlan)
          setWeekDetail(null)
          setCurrentWeekNum(regeneratedPlan.currentWeek)

          const [freshUserResult, freshPlanResult] = await Promise.allSettled([
            getMobileUser(userId),
            getTrainingPlan(userId)
          ])
          if (freshUserResult.status === 'fulfilled') {
            setUser(freshUserResult.value)
          }
          if (freshPlanResult.status === 'fulfilled' && freshPlanResult.value) {
            setPlan(freshPlanResult.value)
            setCurrentWeekNum(freshPlanResult.value.currentWeek)
          }
          const refreshFailed = freshUserResult.status === 'rejected' || freshPlanResult.status === 'rejected'
          setFeedback({
            type: 'success',
            message: refreshFailed
              ? 'User saved and training plan regenerated. Some refreshed details are temporarily unavailable.'
              : 'User saved and training plan regenerated.'
          })
        } catch (err) {
          console.error('Failed to regenerate training plan after saving user', err)
          setFeedback({
            type: 'error',
            message: 'User profile saved, but the training plan could not be regenerated.'
          })
        }
        return
      }

      setFeedback({ type: 'success', message: 'User saved successfully.' })
    } catch (err) {
      console.error('Failed to save user', err)
      setFeedback({ type: 'error', message: 'Unable to save changes.' })
    } finally {
      setSaving(false)
    }
  }

  const handleSaveAccountInfo = async () => {
    if (!userId) return
    setSavingAccountInfo(true)
    setFeedback(null)
    try {
      const trimmedFullName = form.onboardingData.fullName?.trim() || null
      const updated = await updateMobileUser(userId, {
        username: form.username.trim() || null,
        onboardingData: { fullName: trimmedFullName }
      })
      setUser(updated)
      setForm((prev) => ({
        ...prev,
        username: updated.username ?? '',
        onboardingData: {
          ...prev.onboardingData,
          fullName: updated.fullName ?? updated.onboardingData?.fullName ?? null
        }
      }))
      setFeedback({ type: 'success', message: 'Account info updated.' })
    } catch (err) {
      console.error('Failed to save account info', err)
      setFeedback({ type: 'error', message: 'Unable to save account info.' })
    } finally {
      setSavingAccountInfo(false)
    }
  }

  const handleSaveLoginEmail = async () => {
    if (!userId) return

    const trimmedEmail = loginEmail.trim()
    if (!trimmedEmail) {
      setFeedback({ type: 'error', message: 'Enter an email address before saving.' })
      return
    }

    setSavingLoginEmail(true)
    setFeedback(null)
    try {
      const updated = await updateMobileUserIdentity(userId, { email: trimmedEmail })
      setUser(updated)
      setLoginEmail(updated.email ?? '')
      setFeedback({ type: 'success', message: 'Login email updated.' })
    } catch (err) {
      console.error('Failed to save login email', err)
      setFeedback({ type: 'error', message: getApiErrorMessage(err, 'Unable to update login email.') })
    } finally {
      setSavingLoginEmail(false)
    }
  }

  const handleCancelLoginEmail = () => {
    setLoginEmail(user?.email ?? '')
  }

  const handleDelete = async () => {
    if (!userId) return
    setDeleting(true)
    try {
      await deleteMobileUser(userId)
      navigate('/users/mobile', { replace: true })
    } catch (err) {
      console.error('Failed to delete user', err)
      setFeedback({ type: 'error', message: 'Unable to delete user.' })
      setDeleting(false)
      setShowDeleteConfirm(false)
    }
  }

  const handleReset = async () => {
    if (!userId) return
    setResetting(true)
    try {
      await resetMobileUser(userId)
      setFeedback({ type: 'success', message: 'User exercise history and plan have been reset.' })
      setShowResetConfirm(false)
      await loadUser()
    } catch (err) {
      console.error('Failed to reset user', err)
      setFeedback({ type: 'error', message: 'Unable to reset user.' })
    } finally {
      setResetting(false)
    }
  }

  const handleReconcileProjections = async () => {
    if (!userId || reconcilingProjections) return

    setReconcilingProjections(true)
    setFeedback(null)
    try {
      const result = await reconcileMobileUserWorkoutProjections(userId)
      setShowProjectionReconcileConfirm(false)

      const [freshPlanResult, freshWeekResult] = await Promise.allSettled([
        getTrainingPlan(userId),
        getTrainingPlanWeek(userId, currentWeekNum)
      ])
      if (freshPlanResult.status === 'fulfilled') {
        setPlan(freshPlanResult.value)
        if (freshPlanResult.value) {
          setCurrentWeekNum(freshPlanResult.value.currentWeek)
        }
      }
      if (freshWeekResult.status === 'fulfilled') {
        setWeekDetail(freshWeekResult.value)
      }

      setFeedback({
        type: 'success',
        message: formatProjectionReconciliationResult(
          result,
          freshPlanResult.status === 'rejected' || freshWeekResult.status === 'rejected'
        )
      })
    } catch (err) {
      console.error('Failed to reconcile workout projections', err)
      setFeedback({
        type: 'error',
        message: getApiErrorMessage(err, 'Unable to reconcile planner and progress records.')
      })
    } finally {
      setReconcilingProjections(false)
    }
  }

  const handleSendPasswordReset = async () => {
    if (!userId || !user || user.loginType !== 'EmailPassword' || !user.email || sendingPasswordReset) return

    setSendingPasswordReset(true)
    setFeedback(null)
    try {
      await sendMobileUserPasswordReset(userId)
      setFeedback({ type: 'success', message: `Password reset email sent to ${user.email}.` })
    } catch (err) {
      console.error('Failed to send password reset email', err)
      setFeedback({ type: 'error', message: getApiErrorMessage(err, 'Unable to send password reset email.') })
    } finally {
      setSendingPasswordReset(false)
    }
  }

  const handleSetPassword = async () => {
    if (!userId || !user || settingPassword) return

    if (!manualPassword) {
      setFeedback({ type: 'error', message: 'Enter a password before saving.' })
      return
    }

    setSettingPassword(true)
    setFeedback(null)
    try {
      await setMobileUserPassword(userId, manualPassword)
      setManualPassword('')
      setUser((prev) => prev ? { ...prev, loginType: 'EmailPassword' } : prev)
      setFeedback({ type: 'success', message: 'Password updated. The user can sign in with the new password.' })
    } catch (err) {
      console.error('Failed to set mobile user password', err)
      setFeedback({ type: 'error', message: getApiErrorMessage(err, 'Unable to update password.') })
    } finally {
      setSettingPassword(false)
    }
  }

  const handleToggleSubscription = async () => {
    if (!userId || !user) return
    const newIsPaid = !user.isSubscriber
    setTogglingSubscription(true)
    setFeedback(null)
    try {
      await setMobileUserSubscription(userId, newIsPaid)
      setUser((prev) => prev ? { ...prev, isSubscriber: newIsPaid } : prev)
      setFeedback({ type: 'success', message: `User set to ${newIsPaid ? 'Paid' : 'Free'} plan. User must restart the app.` })
    } catch (err) {
      console.error('Failed to toggle subscription', err)
      setFeedback({ type: 'error', message: 'Unable to update subscription.' })
    } finally {
      setTogglingSubscription(false)
    }
  }

  const handleOpenWebSim = async () => {
    if (!userId || !user || !mobileWebBaseUrl || openingWebSim) return

    const displayName = user.username ?? user.email ?? user.userId
    const reason = window.prompt(
      `Reason for opening ${displayName} in the mobile web simulator?`,
      `Investigating support issue for ${displayName}`
    )
    if (reason === null) {
      return
    }

    const trimmedReason = reason.trim()
    if (!trimmedReason) {
      setFeedback({ type: 'error', message: 'Enter a reason before opening a support session.' })
      return
    }

    const simWindow = window.open('about:blank', '_blank')
    setOpeningWebSim(true)
    setFeedback(null)
    try {
      const { ticket } = await createMobileUserImpersonationLink(userId, trimmedReason)
      const url = buildMobileWebImpersonationUrl(ticket, mobileWebBaseUrl)

      if (simWindow) {
        simWindow.opener = null
        simWindow.location.href = url
      } else {
        window.open(url, '_blank', 'noopener,noreferrer')
      }

      setFeedback({
        type: 'success',
        message: 'Mobile web simulator opened. The support link expires in 5 minutes and can only be used once.'
      })
    } catch (err) {
      simWindow?.close()
      console.error('Failed to open mobile web simulator', err)
      setFeedback({ type: 'error', message: 'Unable to open mobile web simulator for this user.' })
    } finally {
      setOpeningWebSim(false)
    }
  }

  const handleProfileImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!userId) return

    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    setProfileImageSaving('upload')
    setFeedback(null)
    try {
      const updated = await uploadMobileUserProfileImage(userId, file)
      setUser(updated)
      setFeedback({ type: 'success', message: 'Profile picture updated.' })
    } catch (err) {
      console.error('Failed to upload profile image', err)
      setFeedback({ type: 'error', message: 'Unable to upload profile picture.' })
    } finally {
      setProfileImageSaving(null)
    }
  }

  const applyDefaultProfilePicture = async (
    picture: DefaultProfilePicture,
    savingKey: string,
    successMessage: string
  ) => {
    if (!userId) return

    setProfileImageSaving(savingKey)
    setFeedback(null)
    try {
      const updated = await setMobileUserDefaultProfilePicture(userId, picture.id)
      setUser(updated)
      setFeedback({ type: 'success', message: successMessage })
    } catch (err) {
      console.error('Failed to reset profile image to default', err)
      setFeedback({ type: 'error', message: 'Unable to reset profile picture.' })
    } finally {
      setProfileImageSaving(null)
    }
  }

  const handleApplyDefaultProfilePicture = async (picture: DefaultProfilePicture) => {
    await applyDefaultProfilePicture(picture, picture.id, `Profile picture reset to ${picture.name}.`)
  }

  const handleApplyRandomDefaultProfilePicture = async () => {
    if (defaultProfilePictures.length === 0) {
      setFeedback({ type: 'error', message: 'No active default profile pictures are available.' })
      return
    }

    const randomPicture = defaultProfilePictures[Math.floor(Math.random() * defaultProfilePictures.length)]
    await applyDefaultProfilePicture(
      randomPicture,
      'random',
      `Profile picture randomly set to ${randomPicture.name}.`
    )
  }

  const updateOnboarding = <K extends keyof OnboardingData>(key: K, value: OnboardingData[K]) => {
    setForm((prev) => ({
      ...prev,
      onboardingData: { ...prev.onboardingData, [key]: value }
    }))
  }

  const toggleArrayItem = <T,>(array: T[], item: T): T[] => {
    return array.includes(item) ? array.filter((i) => i !== item) : [...array, item]
  }

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <p className="text-sm text-muted-foreground">Loading user…</p>
      </div>
    )
  }

  if (error || !user) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/users/mobile')} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to users
        </Button>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-sm text-muted-foreground">{error ?? 'User not found.'}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const coachNotes = user.coachNotes ?? []
  const baseLoginEmail = (user.email ?? '').trim()
  const currentLoginEmail = loginEmail.trim()
  const loginEmailDirty = baseLoginEmail !== currentLoginEmail
  const baseFullName = (user.fullName ?? user.onboardingData?.fullName ?? '').trim()
  const canSendPasswordReset = user.loginType === 'EmailPassword' && Boolean(user.email?.trim())
  const canSetManualPassword = Boolean(user.email?.trim())
  const currentFullName = (form.onboardingData.fullName ?? '').trim()
  const baseUsername = (user.username ?? '').trim()
  const currentUsername = form.username.trim()
  const accountInfoDirty = baseFullName !== currentFullName || baseUsername !== currentUsername

  const baseOnboarding = user.onboardingData ?? {}
  const currentOnboarding = form.onboardingData
  const arraysEqual = <T,>(a: T[] | null | undefined, b: T[] | null | undefined) => {
    const aa = a ?? []
    const bb = b ?? []
    if (aa.length !== bb.length) return false
    for (let i = 0; i < aa.length; i++) {
      if (aa[i] !== bb[i]) return false
    }
    return true
  }
  const formDirty =
    accountInfoDirty ||
    (baseOnboarding.ageRange ?? null) !== (currentOnboarding.ageRange ?? null) ||
    (baseOnboarding.trainingDaysPerWeek ?? null) !== (currentOnboarding.trainingDaysPerWeek ?? null) ||
    !arraysEqual(baseOnboarding.preferredDays, currentOnboarding.preferredDays) ||
    (baseOnboarding.enduranceFrequency ?? null) !== (currentOnboarding.enduranceFrequency ?? null) ||
    (baseOnboarding.hikingFrequency ?? null) !== (currentOnboarding.hikingFrequency ?? null) ||
    (baseOnboarding.measurementUnit ?? null) !== (currentOnboarding.measurementUnit ?? null) ||
    !arraysEqual(baseOnboarding.trueElevationWorkouts, currentOnboarding.trueElevationWorkouts) ||
    !arraysEqual(baseOnboarding.equipmentAccess, currentOnboarding.equipmentAccess) ||
    (baseOnboarding.packWeightOption ?? null) !== (currentOnboarding.packWeightOption ?? null) ||
    (baseOnboarding.packWeightKg ?? null) !== (currentOnboarding.packWeightKg ?? null) ||
    (baseOnboarding.hikeDate ?? null) !== (currentOnboarding.hikeDate ?? null) ||
    (baseOnboarding.hikeDateUnknown ?? null) !== (currentOnboarding.hikeDateUnknown ?? null) ||
    !arraysEqual(baseOnboarding.concerns, currentOnboarding.concerns) ||
    (baseOnboarding.otherConcerns ?? null) !== (currentOnboarding.otherConcerns ?? null)

  const handleCancelAccountInfo = () => {
    setForm((prev) => ({
      ...prev,
      username: user.username ?? '',
      onboardingData: {
        ...prev.onboardingData,
        fullName: baseFullName || null
      }
    }))
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/users/mobile')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <p className="text-sm uppercase tracking-wide text-muted-foreground">Mobile user</p>
            <h1 className="text-2xl font-semibold text-foreground">{user.username ?? user.email ?? user.userId}</h1>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canReconcileProjections && (
            <Button
              type="button"
              onClick={() => setShowProjectionReconcileConfirm(true)}
              disabled={reconcilingProjections}
              className="gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${reconcilingProjections ? 'animate-spin' : ''}`} />
              {reconcilingProjections ? 'Reconciling…' : 'Reconcile planner & progress'}
            </Button>
          )}
          {mobileWebBaseUrl && (
            <Button variant="outline" size="sm" onClick={handleOpenWebSim} disabled={openingWebSim} className="gap-2">
              <MonitorSmartphone className="h-4 w-4" />
              {openingWebSim ? 'Opening…' : 'Open Web Sim'}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setShowResetConfirm(true)} disabled={resetting} className="gap-2">
            <RotateCcw className="h-4 w-4" />
            Reset user
          </Button>
          <Button variant="destructive" size="sm" onClick={() => setShowDeleteConfirm(true)} disabled={deleting} className="gap-2">
            <Trash2 className="h-4 w-4" />
            Delete user
          </Button>
        </div>
      </header>

      {showProjectionReconcileConfirm && (
        <Card className="border-amber-500 bg-amber-500/5">
          <CardContent className="py-4">
            <p className="mb-3 text-sm font-medium text-amber-800">
              Scan this user&apos;s saved workouts, repair matching legacy planner ghosts, and retry incomplete projections? Workout history is preserved. This is safe to run again after it converges; unresolved projections may be retried.
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                onClick={() => void handleReconcileProjections()}
                disabled={reconcilingProjections}
              >
                {reconcilingProjections ? 'Reconciling…' : 'Run reconciliation'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowProjectionReconcileConfirm(false)}
                disabled={reconcilingProjections}
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {showResetConfirm && (
        <Card className="border-amber-500 bg-amber-500/5">
          <CardContent className="py-4">
            <p className="mb-3 text-sm font-medium text-amber-700">
              This will erase all exercise history, training plans, workout definitions, and onboarding data for this user. The user account will be preserved.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleReset} disabled={resetting} className="border-amber-500 text-amber-700 hover:bg-amber-50">
                {resetting ? 'Resetting…' : 'Yes, reset user'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowResetConfirm(false)} disabled={resetting}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {showDeleteConfirm && (
        <Card className="border-destructive bg-destructive/5">
          <CardContent className="py-4">
            <p className="mb-3 text-sm font-medium text-destructive">Are you sure you want to delete this user? This action cannot be undone.</p>
            <div className="flex gap-2">
              <Button variant="destructive" size="sm" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Yes, delete user'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {feedback && (
        <div className={`rounded-lg px-4 py-3 text-sm ${feedback.type === 'success' ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-800'}`}>
          {feedback.message}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <CollapsibleCard
          className="lg:col-span-2"
          title="Account info"
          description="Profile picture, login, and subscription details"
          contentClassName="space-y-6"
        >
          <div className="space-y-4">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-foreground">Profile picture</p>
              <p className="text-sm text-muted-foreground">Current mobile profile image</p>
            </div>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              {user.profileImageUrl ? (
                <img
                  src={user.profileImageUrl}
                  alt={`${user.username ?? user.email ?? user.userId} profile`}
                  className="h-24 w-24 rounded-full border border-border object-cover"
                />
              ) : (
                <div className="flex h-24 w-24 items-center justify-center rounded-full border border-border bg-muted text-muted-foreground">
                  <UserCircle className="h-12 w-12" />
                </div>
              )}
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {user.profileImageUrl ? 'Custom or default image set' : 'No profile picture set'}
                  </p>
                  <p className="text-xs text-muted-foreground">Upload a custom picture or assign one of the active defaults.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <label>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      className="hidden"
                      onChange={handleProfileImageUpload}
                      disabled={profileImageSaving != null}
                    />
                    <span className="inline-flex h-8 cursor-pointer items-center justify-center rounded-md border-2 border-primary/30 bg-transparent px-3 text-xs font-semibold text-primary transition-all hover:border-primary hover:bg-primary/5">
                      {profileImageSaving === 'upload' ? (
                        <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
                      ) : (
                        <Upload className="mr-2 h-4 w-4" />
                      )}
                      Upload custom
                    </span>
                  </label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleApplyRandomDefaultProfilePicture}
                    disabled={profileImageSaving != null || defaultProfilePicturesLoading || defaultProfilePictures.length === 0}
                    type="button"
                    className="gap-2"
                  >
                    {profileImageSaving === 'random' ? (
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
                    ) : (
                      <Shuffle className="h-4 w-4" />
                    )}
                    Random default
                  </Button>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-border bg-muted/10">
              <button
                type="button"
                onClick={() => setDefaultProfilePicturesExpanded((expanded) => !expanded)}
                aria-expanded={defaultProfilePicturesExpanded}
                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
              >
                <span>
                  <span className="block text-sm font-medium text-foreground">Default profile pictures</span>
                  <span className="block text-xs text-muted-foreground">
                    {defaultProfilePicturesLoading
                      ? 'Loading defaults…'
                      : defaultProfilePictures.length > 0
                        ? `${defaultProfilePictures.length} active defaults available`
                        : 'No active defaults uploaded'}
                  </span>
                </span>
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${defaultProfilePicturesExpanded ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                />
              </button>

              {defaultProfilePicturesExpanded && (
                <div className="border-t border-border px-3 py-3">
                  {defaultProfilePicturesLoading ? (
                    <p className="text-sm text-muted-foreground">Loading defaults…</p>
                  ) : defaultProfilePictures.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No active defaults have been uploaded.</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {defaultProfilePictures.map((picture) => {
                        const selected = user.profileImageUrl === picture.imageUrl
                        const saving = profileImageSaving === picture.id
                        return (
                          <button
                            key={picture.id}
                            type="button"
                            onClick={() => handleApplyDefaultProfilePicture(picture)}
                            disabled={profileImageSaving != null}
                            className={`flex min-h-24 flex-col items-center justify-center gap-2 rounded-md border px-2 py-3 text-center text-xs font-medium transition ${
                              selected
                                ? 'border-primary bg-primary/5 text-primary'
                                : 'border-border bg-muted/20 text-foreground hover:border-primary/50 hover:bg-primary/5'
                            }`}
                            aria-label={`Use ${picture.name} as profile picture`}
                          >
                            <img
                              src={picture.imageUrl}
                              alt=""
                              className="h-12 w-12 rounded-full border border-border object-cover"
                            />
                            <span className="max-w-full break-words">{saving ? 'Updating…' : picture.name}</span>
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

          </div>

          <div className="space-y-4 border-t border-border/50 pt-4">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-foreground">Basic account details</p>
              <p className="text-sm text-muted-foreground">Login, identity, and subscription controls</p>
            </div>
            <DetailRow label="User ID" value={user.userId} mono />
            <div className="space-y-2">
              <label htmlFor="login-email" className="text-sm font-medium text-foreground">
                Email address / login
              </label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  id="login-email"
                  type="email"
                  value={loginEmail}
                  onChange={(event) => setLoginEmail(event.target.value)}
                  placeholder="user@example.com"
                  disabled={savingLoginEmail}
                />
                {loginEmailDirty && (
                  <div className="flex gap-2 sm:shrink-0">
                    <Button size="sm" onClick={handleSaveLoginEmail} disabled={savingLoginEmail}>
                      {savingLoginEmail ? 'Saving…' : 'Save login'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleCancelLoginEmail} disabled={savingLoginEmail}>
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <DetailRow
              label="Login type"
              value={formatLoginType(user.loginType)}
              action={
                canSendPasswordReset ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSendPasswordReset}
                    disabled={sendingPasswordReset}
                    className="gap-2"
                  >
                    <Mail className="h-4 w-4" />
                    {sendingPasswordReset ? 'Sending…' : 'Send reset email'}
                  </Button>
                ) : null
              }
            />
            <div className="rounded-lg border border-border bg-muted/10 p-3">
              <div className="flex items-start gap-2">
                <KeyRound className="mt-0.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <div className="min-w-0 flex-1 space-y-3">
                  <div>
                    <label htmlFor="manual-password" className="text-sm font-medium text-foreground">
                      Set password
                    </label>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {canSetManualPassword
                        ? 'Password must be at least 8 characters and include a special character.'
                        : 'Add a valid email address before setting a password.'}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Input
                      id="manual-password"
                      type="text"
                      autoComplete="new-password"
                      value={manualPassword}
                      onChange={(event) => setManualPassword(event.target.value)}
                      placeholder="New password"
                      disabled={!canSetManualPassword || settingPassword}
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleSetPassword}
                      disabled={!canSetManualPassword || settingPassword || manualPassword.length === 0}
                      className="shrink-0 gap-2"
                    >
                      {settingPassword ? 'Saving…' : 'Set password'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <DetailRow label="Created" value={formatDateTime(user.createdAt)} />
            <DetailRow label="Last login" value={formatDateTime(user.lastLoginAt)} />

            <label className="block space-y-2">
              <span className="text-sm font-medium text-foreground">Full name</span>
              <Input
                value={form.onboardingData.fullName ?? ''}
                onChange={(e) => updateOnboarding('fullName', e.target.value || null)}
                placeholder="Enter full name"
              />
            </label>

            <label className="block space-y-2">
              <span className="text-sm font-medium text-foreground">Username</span>
              <Input value={form.username} onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))} placeholder="Enter username" />
            </label>

            {accountInfoDirty && (
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={handleSaveAccountInfo} disabled={savingAccountInfo}>
                  {savingAccountInfo ? 'Saving…' : 'Save'}
                </Button>
                <Button variant="outline" size="sm" onClick={handleCancelAccountInfo} disabled={savingAccountInfo}>
                  Cancel
                </Button>
              </div>
            )}

            {(user.usernameHistory?.length ?? 0) > 0 && (
              <div className="space-y-2">
                <span className="text-xs font-medium text-muted-foreground">Username history</span>
                <div className="space-y-1.5">
                  {user.usernameHistory!.map((entry, i) => (
                    <div key={i} className="flex items-baseline justify-between gap-2 rounded border border-border/40 bg-muted/10 px-3 py-1.5 text-xs">
                      <span className="text-muted-foreground">
                        <span className="font-medium text-foreground">{entry.previousUsername ?? '(none)'}</span>
                        {' → '}
                        <span className="font-medium text-foreground">{entry.newUsername ?? '(none)'}</span>
                      </span>
                      <span className="shrink-0 text-muted-foreground">{formatDateTime(entry.changedAt)}</span>
                    </div>
                  ))}
                </div>
                {user.usernameChangeAllowedAt && new Date(user.usernameChangeAllowedAt) > new Date() && (
                  <div className="flex items-center gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
                    <span>Cooldown active until {formatDateTime(user.usernameChangeAllowedAt)}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs text-amber-800 hover:text-amber-900"
                      onClick={async () => {
                        await resetUsernameCooldown(user.userId)
                        setUser({ ...user, usernameChangeAllowedAt: null })
                      }}
                    >
                      Reset cooldown
                    </Button>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <span className="text-sm font-medium text-foreground">Subscription plan</span>
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${user.isSubscriber ? 'bg-emerald-100 text-emerald-800' : 'bg-zinc-100 text-zinc-600'}`}>
                  {user.isSubscriber ? 'Paid' : 'Free'}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleToggleSubscription}
                  disabled={togglingSubscription}
                >
                  {togglingSubscription ? 'Updating…' : `Switch to ${user.isSubscriber ? 'Free' : 'Paid'}`}
                </Button>
              </div>
            </div>
            {user.subscriptionStoreOfferType === 'OfferCode' && user.subscriptionStoreOfferId ? (
              <DetailRow
                label="Redeemed offer code"
                value={user.subscriptionStoreOfferId}
                mono
              />
            ) : null}
          </div>
        </CollapsibleCard>

        <ActivityLogSection user={user} />

        <Card>
          <CardHeader>
            <CardTitle>Training profile</CardTitle>
            <CardDescription>Fitness preferences and goals</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block space-y-2">
                <span className="text-sm font-medium text-foreground">Age range</span>
                <select
                  value={form.onboardingData.ageRange ?? ''}
                  onChange={(e) => updateOnboarding('ageRange', (e.target.value || null) as AgeRange | null)}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                >
                  <option value="">Not set</option>
                  <option value="Range18To29">18–29</option>
                  <option value="Range30To39">30–39</option>
                  <option value="Range40To49">40–49</option>
                  <option value="Range50Plus">50+</option>
                </select>
              </label>

              <label className="block space-y-2">
                <span className="text-sm font-medium text-foreground">Training days/week</span>
                <Input
                  type="number"
                  min={0}
                  max={7}
                  value={form.onboardingData.trainingDaysPerWeek ?? ''}
                  onChange={(e) => updateOnboarding('trainingDaysPerWeek', e.target.value ? Number(e.target.value) : null)}
                  placeholder="Days"
                />
              </label>
            </div>

            <label className="block space-y-2">
              <span className="text-sm font-medium text-foreground">Measurement unit</span>
              <select
                value={form.onboardingData.measurementUnit ?? ''}
                onChange={(e) => updateOnboarding('measurementUnit', (e.target.value || null) as MeasurementUnit | null)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="">Not set</option>
                {measurementOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>

          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Activity frequency</CardTitle>
            <CardDescription>Current exercise habits</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <label className="block space-y-2">
              <span className="text-sm font-medium text-foreground">Endurance frequency</span>
              <select
                value={form.onboardingData.enduranceFrequency ?? ''}
                onChange={(e) => updateOnboarding('enduranceFrequency', (e.target.value || null) as EnduranceFrequency | null)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="">Not set</option>
                {enduranceOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="block space-y-2">
              <span className="text-sm font-medium text-foreground">Hiking frequency</span>
              <select
                value={form.onboardingData.hikingFrequency ?? ''}
                onChange={(e) => updateOnboarding('hikingFrequency', (e.target.value || null) as HikingFrequency | null)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="">Not set</option>
                {hikingOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>

            <div className="space-y-2">
              <span className="text-sm font-medium text-foreground">Preferred training days</span>
              <div className="flex flex-wrap gap-2">
                {daysOfWeek.map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => updateOnboarding('preferredDays', toggleArrayItem(form.onboardingData.preferredDays ?? [], day))}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                      form.onboardingData.preferredDays?.includes(day)
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-muted/40 text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {day.slice(0, 3)}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Equipment & workouts</CardTitle>
            <CardDescription>Available training resources</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <span className="text-sm font-medium text-foreground">Elevation workout access</span>
              <div className="flex flex-wrap gap-2">
                {elevationWorkoutOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() =>
                      updateOnboarding('trueElevationWorkouts', toggleArrayItem(form.onboardingData.trueElevationWorkouts ?? [], opt.value))
                    }
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                      form.onboardingData.trueElevationWorkouts?.includes(opt.value)
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-muted/40 text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <span className="text-sm font-medium text-foreground">Equipment access</span>
              <div className="flex flex-wrap gap-2">
                {equipmentOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => updateOnboarding('equipmentAccess', toggleArrayItem(form.onboardingData.equipmentAccess ?? [], opt.value))}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                      form.onboardingData.equipmentAccess?.includes(opt.value)
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-muted/40 text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Hike details</CardTitle>
            <CardDescription>Target hike information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <label className="block space-y-2">
              <span className="text-sm font-medium text-foreground">Pack weight option</span>
              <select
                value={form.onboardingData.packWeightOption ?? ''}
                onChange={(e) => updateOnboarding('packWeightOption', (e.target.value || null) as PackWeightOption | null)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="">Not set</option>
                {packWeightOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>

            {form.onboardingData.packWeightOption === 'Custom' && (
              <MeasurementField
                label="Pack weight"
                kind="weight"
                min={0}
                valueMetric={form.onboardingData.packWeightKg ?? null}
                onValueMetricChange={(value) => updateOnboarding('packWeightKg', value)}
                placeholder="Weight"
              />
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block space-y-2">
                <span className="text-sm font-medium text-foreground">Target hike date</span>
                <Input
                  type="date"
                  value={form.onboardingData.hikeDate?.split('T')[0] ?? ''}
                  onChange={(e) => updateOnboarding('hikeDate', e.target.value ? `${e.target.value}T00:00:00Z` : null)}
                />
              </label>

              <label className="flex items-center gap-2 pt-7">
                <input
                  type="checkbox"
                  checked={form.onboardingData.hikeDateUnknown ?? false}
                  onChange={(e) => updateOnboarding('hikeDateUnknown', e.target.checked)}
                  className="h-4 w-4 rounded border-border"
                />
                <span className="text-sm text-foreground">Date unknown</span>
              </label>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Concerns</CardTitle>
            <CardDescription>User&apos;s training concerns</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <span className="text-sm font-medium text-foreground">Primary concerns</span>
              <div className="flex flex-wrap gap-2">
                {concernOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => updateOnboarding('concerns', toggleArrayItem(form.onboardingData.concerns ?? [], opt.value))}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                      form.onboardingData.concerns?.includes(opt.value)
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-muted/40 text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {form.onboardingData.concerns?.includes('Other') && (
              <label className="block space-y-2">
                <span className="text-sm font-medium text-foreground">Other concerns</span>
                <Input
                  value={form.onboardingData.otherConcerns ?? ''}
                  onChange={(e) => updateOnboarding('otherConcerns', e.target.value || null)}
                  placeholder="Describe other concerns"
                />
              </label>
            )}
          </CardContent>
        </Card>

        <TrainingPlanSection
          plan={plan}
          planLoading={planLoading}
          weekDetail={weekDetail}
          weekDetailLoading={weekDetailLoading}
          currentWeekNum={currentWeekNum}
          onWeekChange={setCurrentWeekNum}
          exportingMarkdown={exportingPlanMarkdown}
          onExportMarkdown={handleExportTrainingPlanMarkdown}
        />

        <WorkoutsSection userId={userId ?? ''} />

        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div className="space-y-1">
              <CardTitle>Coach notes</CardTitle>
              <CardDescription>
                Plan adjustments and coaching history
                {!coachNotesExpanded && coachNotes.length > 0 && (
                  <span className="ml-1 text-muted-foreground">({coachNotes.length} notes)</span>
                )}
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCoachNotesExpanded((prev) => !prev)}
            >
              {coachNotesExpanded ? 'Collapse' : 'Expand'}
            </Button>
          </CardHeader>
          {coachNotesExpanded ? (
            <CardContent className="space-y-3">
              {coachNotes.length === 0 ? (
                <p className="text-sm text-muted-foreground">No coach notes recorded yet.</p>
              ) : (
                <div className="space-y-3">
                  {(() => {
                    const now = Date.now()
                    const sorted = [...coachNotes].sort((a, b) => {
                      const tA = new Date(a.effectiveDate ?? a.createdAt).getTime()
                      const tB = new Date(b.effectiveDate ?? b.createdAt).getTime()
                      return tA - tB
                    })
                    const diffs = sorted.map((note) => ({
                      diff: Math.abs(new Date(note.effectiveDate ?? note.createdAt).getTime() - now),
                    }))
                    const sortedDiffs = [...diffs].sort((a, b) => a.diff - b.diff)
                    const threshold = sortedDiffs.length >= 2 ? sortedDiffs[1].diff : sortedDiffs[0]?.diff ?? Infinity
                    const highlightIndices = new Set(
                      diffs.map((d, i) => (d.diff <= threshold ? i : -1)).filter((i) => i >= 0)
                    )

                    return sorted.map((note, index) => {
                      const displayDate = formatDateTime(note.effectiveDate ?? note.createdAt)
                      const reasonLabel = note.reason ? note.reason.replace(/-/g, ' ') : 'coach note'
                      const isHighlighted = highlightIndices.has(index)
                      return (
                        <div
                          key={`${note.createdAt}-${note.reason}-${index}`}
                          className={`rounded-lg border px-4 py-3 ${isHighlighted ? 'border-primary/50 bg-primary/5 ring-1 ring-primary/20' : 'border-border/60 bg-muted/20'}`}
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{reasonLabel}</p>
                            <p className={`text-xs ${isHighlighted ? 'font-medium text-primary' : 'text-muted-foreground'}`}>{displayDate}</p>
                          </div>
                          <p className="mt-2 text-sm text-foreground">{note.message}</p>
                        </div>
                      )
                    })
                  })()}
                </div>
              )}
            </CardContent>
          ) : null}
        </Card>
      </div>

      {formDirty && (
        <div className="sticky bottom-0 flex items-center gap-3 border-t border-border bg-background py-4">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
          <Button variant="outline" onClick={() => loadUser()} disabled={saving}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  )
}

const formatDate = (value: string) => {
  try {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(parsePlanCalendarDate(value))
  } catch {
    return value
  }
}

const formatDateFull = (value: string) => {
  try {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(parsePlanCalendarDate(value))
  } catch {
    return value
  }
}

const planDateKeyPattern = /^(\d{4})-(\d{2})-(\d{2})/

const formatDateKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

const getPlanDateKey = (value: string) => {
  const match = planDateKeyPattern.exec(value)
  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`
  }

  return formatDateKey(new Date(value))
}

const parsePlanCalendarDate = (value: string) => {
  const [year, month, day] = getPlanDateKey(value).split('-').map(Number)
  return new Date(year, month - 1, day)
}

const isPlanDateBeforeToday = (value: string) =>
  getPlanDateKey(value) < formatDateKey(new Date())

const formatExportDate = (value?: string | null) => value ? formatDateFull(value) : 'None'

const formatExportList = (values?: Array<string | null | undefined> | null) => {
  const nonEmptyValues = (values ?? []).filter((value): value is string => Boolean(value?.trim()))
  return nonEmptyValues.length > 0 ? nonEmptyValues.join(', ') : 'None'
}

const formatExportValue = (value?: string | number | boolean | null) => {
  if (value === null || value === undefined || value === '') return 'None'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

const formatRawMetric = (value: number, unit: string) => {
  const rounded = Number(value.toFixed(3))
  return `${rounded.toLocaleString('en-US', { maximumFractionDigits: 3 })} ${unit}`
}

const formatExportMeasurement = (
  value: number | null | undefined,
  kind: 'distance' | 'elevation' | 'weight',
  measurements: MeasurementHelpers
) => {
  const formatted = measurements.format(value, kind)
  if (value === null || value === undefined || Number.isNaN(value)) return formatted

  const raw = formatRawMetric(value, kind === 'distance' ? 'km' : kind === 'elevation' ? 'm' : 'kg')
  return formatted === raw ? formatted : `${formatted} (${raw})`
}

const formatTraceContextMetricsForExport = (
  context: PlanGenerationTraceEntry['contexts'][number],
  measurements: MeasurementHelpers
) => {
  const parts: string[] = []
  if (context.distanceKm != null) {
    parts.push(`distance ${formatExportMeasurement(context.distanceKm, 'distance', measurements)}`)
  }
  if (context.elevationGainMeters != null) {
    parts.push(`elevation ${formatExportMeasurement(context.elevationGainMeters, 'elevation', measurements)}`)
  }
  if (context.packWeightKg != null) {
    parts.push(`pack ${formatExportMeasurement(context.packWeightKg, 'weight', measurements)}`)
  }
  return parts.join('; ')
}

const appendJsonBlock = (lines: string[], title: string, value: unknown) => {
  lines.push(`## ${title}`, '', '```json', JSON.stringify(value, null, 2), '```', '')
}

const appendTraceEntries = (
  lines: string[],
  title: string,
  entries: PlanGenerationTraceEntry[] | null | undefined,
  measurements: MeasurementHelpers
) => {
  if (!entries || entries.length === 0) return

  lines.push(title, '')
  entries.forEach((entry, index) => {
    const datePart = entry.date ? ` (${formatExportDate(entry.date)})` : ''
    lines.push(`${index + 1}. ${entry.rule || 'Unspecified rule'}: ${entry.action || 'Plan rule applied'}${datePart}`)
    if (entry.detail) lines.push(`   - Detail: ${entry.detail}`)

    const contexts = entry.contexts ?? []
    contexts.forEach((context, contextIndex) => {
      lines.push(`   - Context ${contextIndex + 1}: ${context.summary || 'Trace context'}`)
      if (context.date) lines.push(`     - Date: ${formatExportDate(context.date)}`)
      if (context.dayOfWeek) lines.push(`     - Day: ${context.dayOfWeek}`)
      if (context.dayType) lines.push(`     - Day type: ${context.dayType}`)
      if (context.sessionType) lines.push(`     - Session type: ${context.sessionType}`)
      if (context.reason) lines.push(`     - Reason: ${context.reason}`)
      const metrics = formatTraceContextMetricsForExport(context, measurements)
      if (metrics) lines.push(`     - Metrics: ${metrics}`)
      if (context.exercises?.length) lines.push(`     - Exercises: ${context.exercises.join(', ')}`)
    })
  })
  lines.push('')
}

const appendCoachNotes = (lines: string[], notes: TrainingWeekDetail['coachNotes']) => {
  if (notes.length === 0) return

  lines.push('#### Coach Notes', '')
  notes.forEach((note, index) => {
    lines.push(
      `${index + 1}. ${note.reason || 'coach note'} (${formatExportDate(note.effectiveDate ?? note.createdAt)})`,
      `   - Message: ${note.message}`
    )
    if (note.rationale) {
      lines.push('   - Rationale:', '```json', JSON.stringify(note.rationale, null, 2), '```')
    }
  })
  lines.push('')
}

const appendWorkout = (
  lines: string[],
  workout: TrainingWeekDetail['workouts'][number],
  index: number,
  measurements: MeasurementHelpers
) => {
  lines.push(
    `##### Workout ${index + 1}: ${workout.dayOfWeek}, ${formatExportDate(workout.date)}`,
    '',
    `- Workout ID: ${workout.id}`,
    `- Completed: ${formatExportValue(workout.completed)}`,
    `- Completed at: ${workout.completedAt ? formatDateTime(workout.completedAt) : 'None'}`,
    `- Long workout: ${formatExportValue(workout.isLongWorkout)}`,
    `- Milestone: ${workout.isMilestone ? `Yes${workout.milestoneNumber ? ` (${workout.milestoneNumber})` : ''}` : 'No'}`,
    `- Milestone focus: ${formatExportValue(workout.milestoneFocus)}`,
    `- Distance: ${formatExportMeasurement(workout.distanceKm, 'distance', measurements)}`,
    `- Elevation gain: ${formatExportMeasurement(workout.elevationGainMeters, 'elevation', measurements)}`,
    `- Pack weight: ${formatExportMeasurement(workout.packWeightKg, 'weight', measurements)}`,
    `- Elevation type: ${formatExportValue(workout.elevationType)}`,
    `- Estimated duration: ${formatExportValue(workout.estimatedDurationMinutes)} min`,
    `- Notes: ${formatExportValue(workout.notes)}`,
    ''
  )

  if (workout.exercises.length > 0) {
    lines.push('Exercises:')
    const sortedExercises = [...workout.exercises].sort((a, b) => a.order - b.order)
    sortedExercises.forEach((exercise) => {
      const details = formatWorkoutExerciseDetails(exercise, {
        formatDistance: (km) => measurements.format(km, 'distance'),
        formatElevation: (meters) => measurements.format(meters, 'elevation')
      })
      lines.push(
        `- ${exercise.order}. ${exercise.name} (${exercise.code}) - ${exercise.category}; ` +
        `details: ${details || 'None'}; pack: ${exercise.usePack ? 'Yes' : 'No'}; notes: ${exercise.notes ?? 'None'}`
      )
    })
    lines.push('')
  }

  appendTraceEntries(lines, '###### Workout Generation Trace', workout.generationTrace, measurements)
}

const buildGeneratedPlanMarkdownExport = ({
  exportedAt,
  user,
  plan,
  weekDetails,
  measurements
}: {
  exportedAt: Date
  user: MobileUser
  plan: TrainingPlan
  weekDetails: TrainingWeekDetail[]
  measurements: MeasurementHelpers
}) => {
  const detailByWeek = new Map(weekDetails.map((week) => [week.weekNumber, week]))
  const totalWeekTraceEntries = weekDetails.reduce((sum, week) => sum + week.generationTrace.length, 0)
  const totalWorkoutTraceEntries = weekDetails.reduce(
    (sum, week) => sum + week.workouts.reduce((workoutSum, workout) => workoutSum + workout.generationTrace.length, 0),
    0
  )
  const onboarding = user.onboardingData ?? {}
  const lines: string[] = [
    '# HikeStrong User Training Plan Export',
    '',
    `- Exported at: ${exportedAt.toLocaleString()}`,
    '- Source: Base Camp mobile user detail',
    `- Display units: ${measurements.unit}`,
    ''
  ]

  lines.push(
    '## User',
    '',
    `- User ID: ${user.userId}`,
    `- Username: ${formatExportValue(user.username)}`,
    `- Email: ${formatExportValue(user.email)}`,
    `- Full name: ${formatExportValue(user.fullName ?? onboarding.fullName)}`,
    `- Login type: ${formatLoginType(user.loginType)}`,
    `- Subscriber: ${formatExportValue(user.isSubscriber)}`,
    `- Onboarding progress: ${formatExportValue(user.onboardingProgress)}`,
    ''
  )

  lines.push(
    '## Onboarding Inputs',
    '',
    `- Age range: ${formatExportValue(onboarding.ageRange)}`,
    `- Training days/week: ${formatExportValue(onboarding.trainingDaysPerWeek)}`,
    `- Preferred days: ${formatExportList(onboarding.preferredDays)}`,
    `- Endurance frequency: ${formatExportValue(onboarding.enduranceFrequency)}`,
    `- Hiking frequency: ${formatExportValue(onboarding.hikingFrequency)}`,
    `- Measurement unit: ${formatExportValue(onboarding.measurementUnit)}`,
    `- True elevation workouts: ${formatExportList(onboarding.trueElevationWorkouts)}`,
    `- Equipment access: ${formatExportList(onboarding.equipmentAccess)}`,
    `- Pack weight option: ${formatExportValue(onboarding.packWeightOption)}`,
    `- Pack weight: ${formatExportMeasurement(onboarding.packWeightKg, 'weight', measurements)}`,
    `- Hike date: ${formatExportDate(onboarding.hikeDate)}`,
    `- Hike date unknown: ${formatExportValue(onboarding.hikeDateUnknown)}`,
    `- Concerns: ${formatExportList(onboarding.concerns)}`,
    `- Other concerns: ${formatExportValue(onboarding.otherConcerns)}`,
    ''
  )

  lines.push(
    '## Plan Summary',
    '',
    `- Plan ID: ${plan.id}`,
    `- Goal: ${plan.goalName}`,
    `- Goal type: ${plan.goalType}`,
    `- Plan dates: ${formatExportDate(plan.planStartDate)} to ${formatExportDate(plan.planEndDate)}`,
    `- Target hike date: ${formatExportDate(plan.targetHikeDate)}`,
    `- Duration: ${plan.durationWeeks} weeks`,
    `- Current week: ${plan.currentWeek}`,
    `- Created: ${formatDateTime(plan.createdAt)}`,
    `- Updated: ${plan.updatedAt ? formatDateTime(plan.updatedAt) : 'None'}`,
    `- Target distance: ${formatExportMeasurement(plan.targets.totalDistanceKm, 'distance', measurements)}`,
    `- Target elevation gain: ${formatExportMeasurement(plan.targets.totalElevationGainMeters, 'elevation', measurements)}`,
    `- Target pack weight: ${formatExportMeasurement(plan.targets.targetPackWeightKg, 'weight', measurements)}`,
    ''
  )

  lines.push(
    '## Trace Coverage',
    '',
    `- Plan-level trace entries: ${plan.generationTrace.length}`,
    `- Week-level trace entries: ${totalWeekTraceEntries}`,
    `- Workout-level trace entries: ${totalWorkoutTraceEntries}`,
    `- Week details exported: ${weekDetails.length} of ${plan.weeks.length}`,
    ''
  )

  appendTraceEntries(lines, '## Plan Generation Trace', plan.generationTrace, measurements)

  lines.push('## Week-by-Week Breakdown', '')
  plan.weeks.forEach((weekSummary) => {
    const weekDetail = detailByWeek.get(weekSummary.weekNumber)

    lines.push(
      `### Week ${weekSummary.weekNumber}: ${weekSummary.phase}`,
      '',
      `- Date range: ${formatExportDate(weekSummary.weekStartDate)} to ${formatExportDate(weekSummary.weekEndDate)}`,
      `- Target distance: ${formatExportMeasurement(weekSummary.targetDistanceKm, 'distance', measurements)}`,
      `- Target elevation gain: ${formatExportMeasurement(weekSummary.targetElevationGainMeters, 'elevation', measurements)}`,
      `- Target pack weight: ${formatExportMeasurement(weekSummary.targetPackWeightKg, 'weight', measurements)}`,
      `- Actual distance: ${formatExportMeasurement(weekSummary.actualDistanceKm, 'distance', measurements)}`,
      `- Actual elevation gain: ${formatExportMeasurement(weekSummary.actualElevationGainMeters, 'elevation', measurements)}`,
      `- Workouts completed: ${weekSummary.completedWorkoutCount}/${weekSummary.workoutCount}`,
      `- Revision state: ${weekSummary.revisionState}`,
      `- Flags: ${formatExportList(weekSummary.flags)}`,
      `- Coach note count: ${weekSummary.coachNoteCount}`,
      `- Generation trace count: ${weekSummary.generationTraceCount}`,
      ''
    )

    if (!weekDetail) {
      lines.push('Week details were not available for this export.', '')
      return
    }

    lines.push(
      `- Active goal distance: ${formatExportMeasurement(weekDetail.prescribedDistanceKm, 'distance', measurements)}`,
      `- Active goal elevation gain: ${formatExportMeasurement(weekDetail.prescribedElevationGainMeters, 'elevation', measurements)}`,
      `- Active goal pack weight: ${formatExportMeasurement(weekDetail.prescribedPackWeightKg, 'weight', measurements)}`,
      `- Active goal weighted workouts: ${formatExportValue(weekDetail.prescribedWeightedWorkouts)}`,
      `- Active goal lunge workouts: ${formatExportValue(weekDetail.prescribedLungeWorkouts)}`,
      `- Active goal balance workouts: ${formatExportValue(weekDetail.prescribedBalanceWorkouts)}`,
      `- Active goal HIIT workouts: ${formatExportValue(weekDetail.prescribedHiitWorkouts)}`,
      `- Original prescribed distance: ${formatExportMeasurement(weekDetail.originalPrescribedDistanceKm, 'distance', measurements)}`,
      `- Original prescribed elevation gain: ${formatExportMeasurement(weekDetail.originalPrescribedElevationGainMeters, 'elevation', measurements)}`,
      `- Original prescribed pack weight: ${formatExportMeasurement(weekDetail.originalPrescribedPackWeightKg, 'weight', measurements)}`,
      `- Original prescribed weighted workouts: ${formatExportValue(weekDetail.originalPrescribedWeightedWorkouts)}`,
      `- Original prescribed lunge workouts: ${formatExportValue(weekDetail.originalPrescribedLungeWorkouts)}`,
      `- Original prescribed balance workouts: ${formatExportValue(weekDetail.originalPrescribedBalanceWorkouts)}`,
      `- Original prescribed HIIT workouts: ${formatExportValue(weekDetail.originalPrescribedHiitWorkouts)}`,
      `- Intensity factor: ${weekDetail.intensityFactor}`,
      ''
    )

    appendTraceEntries(lines, '#### Week Generation Trace', weekDetail.generationTrace, measurements)

    if (weekDetail.workouts.length > 0) {
      lines.push('#### Workouts', '')
      const sortedWorkouts = [...weekDetail.workouts].sort((a, b) =>
        getPlanDateKey(a.date).localeCompare(getPlanDateKey(b.date))
      )
      sortedWorkouts.forEach((workout, index) => appendWorkout(lines, workout, index, measurements))
    } else {
      lines.push('#### Workouts', '', 'No workouts scheduled this week.', '')
    }

    appendCoachNotes(lines, weekDetail.coachNotes)
  })

  appendJsonBlock(lines, 'Raw Export Data', {
    exportedAt: exportedAt.toISOString(),
    user,
    plan,
    weekDetails
  })

  return `${lines.join('\n').trim()}\n`
}

const slugifyExportPart = (value: string | null | undefined, fallback: string) =>
  (value ?? fallback)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48) || fallback

const createGeneratedPlanExportFilename = (user: MobileUser, plan: TrainingPlan, exportedAt: Date) => {
  const stamp = exportedAt.toISOString().replace(/[:.]/g, '-')
  const userSlug = slugifyExportPart(user.username ?? user.email ?? user.userId, 'user')
  const goalSlug = slugifyExportPart(plan.goalName, 'plan')
  return `hikestrong-user-plan-${userSlug}-${goalSlug}-${stamp}.md`
}

const downloadMarkdownFile = (filename: string, markdown: string) => {
  const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 0)
}

const GenerationTraceNotes = ({ entries }: { entries?: PlanGenerationTraceEntry[] | null }) => {
  if (!entries || entries.length === 0) return null

  return (
    <div className="mt-2 rounded-md border border-sky-200 bg-sky-50/70 px-3 py-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-sky-900">
        Generation notes
      </p>
      <div className="mt-1.5 space-y-2">
        {entries.map((entry, index) => (
          <div key={`${entry.rule}-${entry.date ?? 'week'}-${entry.action}-${index}`} className="text-xs">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-medium text-sky-950">{entry.action || 'Plan rule applied'}</span>
              {entry.rule && (
                <span className="font-mono text-[11px] text-sky-700">{entry.rule}</span>
              )}
            </div>
            {entry.detail && <p className="mt-0.5 text-sky-900">{entry.detail}</p>}
            {entry.contexts.length > 0 && (
              <div className="mt-1 space-y-0.5 text-sky-800">
                {entry.contexts.slice(0, 3).map((context, contextIndex) => (
                  <p key={`${context.summary}-${contextIndex}`}>
                    {context.reason ? `${context.reason}: ` : ''}
                    {context.summary}
                  </p>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

const TrainingPlanSection = ({
  plan,
  planLoading,
  weekDetail,
  weekDetailLoading,
  currentWeekNum,
  onWeekChange,
  exportingMarkdown,
  onExportMarkdown
}: {
  plan: TrainingPlan | null
  planLoading: boolean
  weekDetail: TrainingWeekDetail | null
  weekDetailLoading: boolean
  currentWeekNum: number
  onWeekChange: (week: number) => void
  exportingMarkdown: boolean
  onExportMarkdown: () => void
}) => {
  const measurements = useMeasurements()

  if (planLoading) {
    return (
      <CollapsibleCard
        className="lg:col-span-2"
        title="Training plan"
        description="Scheduled workouts and progress"
        contentClassName="py-12 text-center"
      >
          <p className="text-sm text-muted-foreground">Loading training plan…</p>
      </CollapsibleCard>
    )
  }

  if (!plan) {
    return (
      <CollapsibleCard
        className="lg:col-span-2"
        title="Training plan"
        description="Scheduled workouts and progress"
      >
          <p className="text-sm text-muted-foreground">No active training plan.</p>
      </CollapsibleCard>
    )
  }

  const weekSummary = plan.weeks.find(w => w.weekNumber === currentWeekNum)
  const isCurrentWeek = currentWeekNum === plan.currentWeek

  return (
    <CollapsibleCard
      className="lg:col-span-2"
      title="Training plan"
      description={
        <>
            {plan.goalName} · {plan.durationWeeks} weeks · {formatDateFull(plan.planStartDate)} – {formatDateFull(plan.planEndDate)}
            {plan.targetHikeDate && ` · Hike: ${formatDateFull(plan.targetHikeDate)}`}
            {` · Displayed in ${measurements.unit === 'Imperial' ? 'Imperial (mi, ft, lb)' : 'Metric (km, m, kg)'}`}
        </>
      }
      contentClassName="space-y-4"
      actions={
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onExportMarkdown}
          disabled={exportingMarkdown}
          className="shrink-0 gap-2"
        >
          {exportingMarkdown ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Download className="h-4 w-4" aria-hidden="true" />
          )}
          {exportingMarkdown ? 'Preparing…' : 'Download markdown'}
        </Button>
      }
    >
        {/* Plan targets summary */}
        <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
          <span>Target distance: <span className="font-medium text-foreground">{measurements.format(plan.targets.totalDistanceKm, 'distance')}</span></span>
          <span>Elevation: <span className="font-medium text-foreground">{measurements.format(plan.targets.totalElevationGainMeters, 'elevation')}</span></span>
          <span>Pack: <span className="font-medium text-foreground">{measurements.format(plan.targets.targetPackWeightKg, 'weight')}</span></span>
        </div>

        <GenerationTraceNotes entries={plan.generationTrace} />

        {/* Week navigation */}
        <div className="flex items-center justify-between rounded-lg border border-border bg-muted/20 px-4 py-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onWeekChange(currentWeekNum - 1)}
            disabled={currentWeekNum <= 1}
            className="gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Prev
          </Button>
          <div className="text-center">
            <span className="text-sm font-semibold">
              Week {currentWeekNum} of {plan.durationWeeks}
            </span>
            {weekSummary && (
              <span className="ml-2 text-xs text-muted-foreground">
                ({weekSummary.phase})
              </span>
            )}
            {isCurrentWeek && (
              <span className="ml-2 inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                Current
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onWeekChange(currentWeekNum + 1)}
            disabled={currentWeekNum >= plan.durationWeeks}
            className="gap-1"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Week summary bar */}
        {weekSummary && (
          <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
            <span>Target: {measurements.format(weekSummary.targetDistanceKm, 'distance')}, {measurements.format(weekSummary.targetElevationGainMeters, 'elevation')}</span>
            <span>Pack: {measurements.format(weekSummary.targetPackWeightKg, 'weight')}</span>
            <span>Workouts: {weekSummary.completedWorkoutCount}/{weekSummary.workoutCount} completed</span>
            {weekSummary.revisionState === 'Revised' && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">Revised</span>
            )}
            {weekSummary.generationTraceCount > 0 && (
              <span>{weekSummary.generationTraceCount} generation notes</span>
            )}
          </div>
        )}

        {/* Week detail with workouts */}
        {weekDetailLoading ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Loading workouts…</p>
        ) : weekDetail ? (
          <div className="space-y-3">
            {weekDetail.workouts.length === 0 ? (
              <p className="text-sm text-muted-foreground">No workouts scheduled this week.</p>
            ) : (
              weekDetail.workouts.map((workout) => {
                const isPast = isPlanDateBeforeToday(workout.date)
                return (
                  <div
                    key={workout.id}
                    className={`rounded-lg border px-4 py-3 ${
                      workout.completed
                        ? 'border-emerald-200 bg-emerald-50/50'
                        : isPast
                          ? 'border-border/40 bg-muted/10 opacity-60'
                          : 'border-border/60 bg-background'
                    }`}
                  >
                    {/* Workout header */}
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {workout.completed ? (
                          <CheckCircle className="h-4 w-4 text-emerald-600" />
                        ) : (
                          <Circle className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span className={`text-sm font-medium ${workout.completed ? 'text-emerald-800 line-through' : 'text-foreground'}`}>
                          {workout.dayOfWeek}
                        </span>
                        <span className="text-xs text-muted-foreground">{formatDate(workout.date)}</span>
                        <span className={`text-xs font-medium ${workout.completed ? 'text-emerald-700' : 'text-muted-foreground'}`}>
                          {workout.completed ? 'Completed' : 'Upcoming'}
                        </span>
                        {workout.isMilestone && !workout.milestoneHiddenFromUser && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                            <Star className="h-3 w-3" />
                            Milestone {workout.milestoneNumber}
                          </span>
                        )}
                        {workout.isLongWorkout && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                            <Timer className="h-3 w-3" />
                            Long
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        ~{workout.estimatedDurationMinutes} min
                      </span>
                    </div>

                    {/* Workout targets */}
                    <div className="mt-1.5 flex flex-wrap gap-3 text-xs text-muted-foreground">
                      <span>{measurements.format(workout.distanceKm, 'distance')}</span>
                      <span>{measurements.format(workout.elevationGainMeters, 'elevation')}</span>
                      <span>{measurements.format(workout.packWeightKg, 'weight')} pack</span>
                      {workout.completed && workout.perceivedDifficulty && (
                        <span className="text-emerald-700">Felt: {workout.perceivedDifficulty}</span>
                      )}
                    </div>

                    {/* Exercise list */}
                    {workout.exercises.length > 0 && (
                      <div className="mt-2 space-y-1 border-t border-border/30 pt-2">
                        {workout.exercises
                          .sort((a, b) => a.order - b.order)
                          .map((ex) => {
                            const details = formatWorkoutExerciseDetails(ex, {
                              formatDistance: (km) => measurements.format(km, 'distance'),
                              formatElevation: (meters) => measurements.format(meters, 'elevation')
                            })

                            return (
                              <div key={`${ex.code}-${ex.order}`} className="flex items-baseline justify-between text-xs">
                                <span className={`${workout.completed ? 'text-emerald-700 line-through' : 'text-foreground'}`}>
                                  {ex.name}
                                  {ex.usePack && <span className="ml-1 text-muted-foreground">(pack)</span>}
                                </span>
                                <span className="shrink-0 text-muted-foreground">{details}</span>
                              </div>
                            )
                          })}
                      </div>
                    )}

                    <GenerationTraceNotes entries={workout.generationTrace} />
                  </div>
                )
              })
            )}

            <GenerationTraceNotes entries={weekDetail.generationTrace} />

            {/* Week coach notes */}
            {weekDetail.coachNotes.length > 0 && (
              <div className="space-y-2 border-t border-border/40 pt-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Week coach notes</p>
                {weekDetail.coachNotes.map((note, i) => (
                  <div key={`${note.createdAt}-${i}`} className="rounded border border-border/40 bg-muted/10 px-3 py-2 text-xs">
                    <span className="font-medium text-foreground">{note.reason.replace(/-/g, ' ')}</span>
                    <span className="ml-2 text-muted-foreground">{formatDate(note.effectiveDate ?? note.createdAt)}</span>
                    <p className="mt-1 text-foreground">{note.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <p className="py-6 text-center text-sm text-muted-foreground">Unable to load week details.</p>
        )}
    </CollapsibleCard>
  )
}

const DetailRow = ({
  label,
  value,
  mono,
  action
}: {
  label: string
  value: string
  mono?: boolean
  action?: ReactNode
}) => (
  <div className="rounded-xl border border-border/60 bg-muted/20 px-4 py-3">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className={`break-words text-sm font-medium text-foreground ${mono ? 'font-mono' : ''}`}>{value}</p>
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  </div>
)

const ACTIVITY_LOG_PAGE_SIZE = 50
// Leave one row under the API's 200-row service cap for its hasMore look-ahead.
const ACTIVITY_LOG_EXPORT_PAGE_SIZE = 199

const createActivityLogExportFilename = (user: MobileUser, exportedAt: Date) => {
  const stamp = exportedAt.toISOString().replace(/[:.]/g, '-')
  const userSlug = slugifyExportPart(user.username ?? user.email ?? user.userId, 'user')
  return `hikestrong-activity-log-${userSlug}-${stamp}.csv`
}

const downloadActivityLogCsv = (user: MobileUser, entries: UserActivityLogEntry[]) => {
  downloadCsv(
    [
      'occurred_at_utc',
      'received_at_utc',
      'event_type',
      'source',
      'platform',
      'app_version',
      'entity_type',
      'entity_id',
      'properties_json',
      'event_id',
      'user_id'
    ],
    entries.map((entry) => [
      entry.occurredAt,
      entry.receivedAt,
      entry.eventType,
      entry.source,
      entry.platform ?? '',
      entry.appVersion ?? '',
      entry.entityType ?? '',
      entry.entityId ?? '',
      entry.properties == null ? '' : JSON.stringify(entry.properties),
      entry.id,
      entry.userId
    ]),
    createActivityLogExportFilename(user, new Date())
  )
}

const ActivityLogSection = ({ user }: { user: MobileUser }) => {
  const userId = user.userId
  const [entries, setEntries] = useState<UserActivityLogEntry[]>([])
  const [skip, setSkip] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [exportError, setExportError] = useState<string | null>(null)
  const [eventType, setEventType] = useState('')

  const loadPage = useCallback(
    async (nextSkip: number) => {
      if (!userId) return
      setLoading(true)
      setLoadError(null)
      try {
        const res = await getUserActivityLog(userId, {
          skip: nextSkip,
          limit: ACTIVITY_LOG_PAGE_SIZE,
          eventType: eventType || undefined
        })
        setEntries(res.entries)
        setSkip(res.skip)
        setHasMore(res.hasMore)
      } catch (err) {
        console.error('Failed to load user activity log', err)
        setLoadError('Unable to load activity log.')
      } finally {
        setLoading(false)
      }
    },
    [eventType, userId]
  )

  useEffect(() => {
    void loadPage(0)
  }, [loadPage])

  const handleDownload = useCallback(async () => {
    if (!userId) return

    setExporting(true)
    setExportError(null)
    try {
      const allEntries: UserActivityLogEntry[] = []
      const seenEntryIds = new Set<string>()
      let nextSkip = 0
      let hasAnotherPage = true

      while (hasAnotherPage) {
        const response = await getUserActivityLog(userId, {
          skip: nextSkip,
          limit: ACTIVITY_LOG_EXPORT_PAGE_SIZE
        })

        response.entries.forEach((entry) => {
          if (!seenEntryIds.has(entry.id)) {
            seenEntryIds.add(entry.id)
            allEntries.push(entry)
          }
        })

        if (response.hasMore && response.entries.length === 0) {
          throw new Error('Activity log pagination returned an empty page before completion.')
        }

        nextSkip = response.skip + response.entries.length
        hasAnotherPage = response.hasMore
      }

      downloadActivityLogCsv(user, allEntries)
    } catch (err) {
      console.error('Failed to download full user activity log', err)
      setExportError('Unable to download the full activity log.')
    } finally {
      setExporting(false)
    }
  }, [user, userId])

  return (
    <CollapsibleCard
      className="lg:col-span-2"
      title="Activity log"
      description="Device and account events for debugging this user."
      icon={<Activity className="h-5 w-5 text-primary" aria-hidden="true" />}
      contentClassName="space-y-3"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="Filter activity events"
            value={eventType}
            onChange={(event) => setEventType(event.target.value)}
            className="h-9 rounded-md border border-border bg-background px-3 text-sm"
          >
            <option value="">All events</option>
            {activityEventOptions.map((option) => (
              <option key={option} value={option}>
                {formatActivityEventLabel(option)}
              </option>
            ))}
          </select>
          <Button variant="outline" size="sm" onClick={() => loadPage(0)} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleDownload}
            disabled={exporting}
            className="gap-2"
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Download className="h-4 w-4" aria-hidden="true" />
            )}
            {exporting ? 'Downloading…' : 'Download full log'}
          </Button>
        </div>
      }
    >
        {loadError && (
          <p className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{loadError}</p>
        )}
        {exportError && (
          <p className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{exportError}</p>
        )}

        {loading && entries.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Loading activity…</p>
        ) : entries.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No activity recorded yet.</p>
        ) : (
          <div className="space-y-2">
            {entries.map((entry) => {
              const properties = Object.entries(entry.properties ?? {}).slice(0, 12)
              return (
                <div key={entry.id} className="rounded-lg border border-border/60 bg-background px-4 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                        {formatActivityEventLabel(entry.eventType)}
                      </span>
                      <span className="font-mono text-xs text-muted-foreground">{entry.eventType}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{formatDateTime(entry.occurredAt)}</span>
                  </div>

                  <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                    <span>Source: <span className="font-medium text-foreground">{entry.source}</span></span>
                    {entry.platform && (
                      <span>Platform: <span className="font-medium text-foreground">{entry.platform}</span></span>
                    )}
                    {entry.appVersion && (
                      <span>App: <span className="font-medium text-foreground">{entry.appVersion}</span></span>
                    )}
                    {entry.entityType && (
                      <span>
                        Entity: <span className="font-medium text-foreground">{entry.entityType}{entry.entityId ? `/${entry.entityId}` : ''}</span>
                      </span>
                    )}
                    <span>Received: <span className="font-medium text-foreground">{formatDateTime(entry.receivedAt)}</span></span>
                  </div>

                  {properties.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {properties.map(([key, value]) => (
                        <span key={key} className="max-w-full break-words rounded border border-border/50 bg-muted/20 px-2 py-0.5 text-xs text-muted-foreground">
                          <span className="font-medium text-foreground">{key}</span>: {formatActivityPropertyValue(value)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        <div className="flex items-center justify-between border-t border-border/40 pt-3">
          <Button
            variant="outline"
            size="sm"
            disabled={loading || skip <= 0}
            onClick={() => loadPage(Math.max(0, skip - ACTIVITY_LOG_PAGE_SIZE))}
          >
            Newer
          </Button>
          <span className="text-xs text-muted-foreground">
            Showing {entries.length === 0 ? 0 : skip + 1}–{skip + entries.length}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={loading || !hasMore}
            onClick={() => loadPage(skip + ACTIVITY_LOG_PAGE_SIZE)}
          >
            Older
          </Button>
        </div>
    </CollapsibleCard>
  )
}

const WORKOUTS_PAGE_SIZE = 25

const formatDuration = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0:00'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  return h > 0
    ? `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
    : `${m}:${s.toString().padStart(2, '0')}`
}

const WorkoutsSection = ({ userId }: { userId: string }) => {
  const measurements = useMeasurements()
  const [workouts, setWorkouts] = useState<AdminUserWorkoutSummary[]>([])
  const [skip, setSkip] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [selectedWorkoutId, setSelectedWorkoutId] = useState<string | null>(null)
  const [trace, setTrace] = useState<WorkoutGpsTrace | null>(null)
  const [traceLoading, setTraceLoading] = useState(false)
  const [traceError, setTraceError] = useState<string | null>(null)

  const loadPage = useCallback(
    async (nextSkip: number) => {
      if (!userId) return
      setLoading(true)
      setLoadError(null)
      try {
        const res = await getUserWorkouts(userId, { skip: nextSkip, limit: WORKOUTS_PAGE_SIZE })
        setWorkouts(res.workouts)
        setSkip(res.skip)
        setHasMore(res.hasMore)
      } catch (err) {
        console.error('Failed to load user workouts', err)
        setLoadError('Unable to load workouts.')
      } finally {
        setLoading(false)
      }
    },
    [userId]
  )

  useEffect(() => {
    void loadPage(0)
  }, [loadPage])

  const selectedWorkout = useMemo(
    () => workouts.find((w) => w.id === selectedWorkoutId) ?? null,
    [workouts, selectedWorkoutId]
  )

  const handleSelect = useCallback(
    async (workout: AdminUserWorkoutSummary) => {
      if (selectedWorkoutId === workout.id) {
        setSelectedWorkoutId(null)
        setTrace(null)
        setTraceError(null)
        return
      }
      setSelectedWorkoutId(workout.id)
      setTrace(null)
      setTraceError(null)
      if (!workout.hasGpsTrace) {
        return
      }
      setTraceLoading(true)
      try {
        const data = await getUserWorkoutGpsTrace(userId, workout.id)
        if (!data) {
          setTraceError('No GPS trace recorded for this workout.')
        } else {
          setTrace(data)
        }
      } catch (err) {
        console.error('Failed to load gps trace', err)
        setTraceError('Unable to load GPS trace.')
      } finally {
        setTraceLoading(false)
      }
    },
    [selectedWorkoutId, userId]
  )

  return (
    <CollapsibleCard
      className="lg:col-span-2"
      title="Recorded workouts"
      description="Tap a workout to inspect GPS samples on a map. Hike sessions with location tracking show the route, paused segments, and rejected points."
      contentClassName="space-y-3"
    >
        {loadError && (
          <p className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{loadError}</p>
        )}

        {loading && workouts.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Loading workouts…</p>
        ) : workouts.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No workouts recorded yet.</p>
        ) : (
          <div className="space-y-2">
            {workouts.map((workout) => {
              const isSelected = selectedWorkoutId === workout.id
              return (
                <div key={workout.id} className="space-y-2">
                  <button
                    type="button"
                    onClick={() => handleSelect(workout)}
                    className={`w-full rounded-lg border px-4 py-3 text-left transition ${
                      isSelected
                        ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                        : 'border-border/60 bg-background hover:border-border'
                    }`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground">{workout.exerciseName}</span>
                        <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">{workout.exerciseCode}</span>
                        {workout.hasGpsTrace && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800">
                            <MapIcon className="h-3 w-3" />
                            GPS
                          </span>
                        )}
                        {workout.isPublic && (
                          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">Public</span>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">{formatDateTime(workout.completedAt)}</span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-3 text-xs text-muted-foreground">
                      <span>Duration: <span className="font-medium text-foreground">{formatDuration(workout.durationSeconds)}</span></span>
                      {workout.distanceKm != null && (
                        <span>Distance: <span className="font-medium text-foreground">{measurements.format(workout.distanceKm, 'distance')}</span></span>
                      )}
                      {workout.elevationMeters != null && (
                        <span>Elevation: <span className="font-medium text-foreground">{measurements.format(workout.elevationMeters, 'elevation')}</span></span>
                      )}
                      {workout.packWeightKg != null && (
                        <span>Pack: <span className="font-medium text-foreground">{measurements.format(workout.packWeightKg, 'weight')}</span></span>
                      )}
                      {workout.difficultyRating != null && (
                        <span>Felt: <span className="font-medium text-foreground">{workout.difficultyRating}/5</span></span>
                      )}
                    </div>
                  </button>

                  {isSelected && (
                    <div className="rounded-lg border border-border bg-muted/10 p-3">
                      {!selectedWorkout?.hasGpsTrace ? (
                        <p className="text-sm text-muted-foreground">
                          This workout has no GPS trace. Only hike-style workouts started with location tracking enabled are recorded.
                        </p>
                      ) : traceLoading ? (
                        <p className="text-sm text-muted-foreground">Loading GPS samples…</p>
                      ) : traceError ? (
                        <p className="text-sm text-rose-800">{traceError}</p>
                      ) : trace ? (
                        <WorkoutGpsDebugView trace={trace} />
                      ) : null}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        <div className="flex items-center justify-between border-t border-border/40 pt-3">
          <Button
            variant="outline"
            size="sm"
            disabled={loading || skip <= 0}
            onClick={() => loadPage(Math.max(0, skip - WORKOUTS_PAGE_SIZE))}
          >
            Newer
          </Button>
          <span className="text-xs text-muted-foreground">
            Showing {workouts.length === 0 ? 0 : skip + 1}–{skip + workouts.length}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={loading || !hasMore}
            onClick={() => loadPage(skip + WORKOUTS_PAGE_SIZE)}
          >
            Older
          </Button>
        </div>
    </CollapsibleCard>
  )
}
